'use client';

import { useState, useRef, useCallback } from 'react';

interface Props {
  onActivate: () => void;
}

export default function PanicButton({ onActivate }: Props) {
  const [pressing, setPressing] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startRef = useRef<number>(0);
  const HOLD_DURATION = 800;

  const startPress = useCallback(() => {
    setPressing(true);
    startRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.min(elapsed / HOLD_DURATION, 1);
      setProgress(pct);
      if (pct >= 1) {
        clearInterval(intervalRef.current!);
        setPressing(false);
        setProgress(0);
        if (navigator.vibrate) navigator.vibrate([30, 20, 60]);
        onActivate();
      }
    }, 16);
  }, [onActivate]);

  const cancelPress = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setPressing(false);
    setProgress(0);
  }, []);

  const circumference = 2 * Math.PI * 68;
  const dashOffset = circumference * (1 - progress);

  return (
    <div className="relative flex items-center justify-center w-48 h-48">
      {/* Pulse rings */}
      <div
        className={`absolute inset-0 rounded-full border-2 border-[hsl(var(--primary)/0.3)] transition-opacity duration-300 ${pressing ? 'opacity-0' : 'panic-ring'}`}
      />
      <div
        className={`absolute inset-[-8px] rounded-full border border-[hsl(var(--primary)/0.15)] transition-opacity duration-300 ${pressing ? 'opacity-0' : 'panic-ring'}`}
        style={{ animationDelay: '0.5s' }}
      />

      {/* Progress ring */}
      <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 152 152">
        <circle
          cx="76" cy="76" r="68"
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth="2"
        />
        <circle
          cx="76" cy="76" r="68"
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          className="transition-all duration-[16ms]"
        />
      </svg>

      {/* Button */}
      <button
        onPointerDown={startPress}
        onPointerUp={cancelPress}
        onPointerLeave={cancelPress}
        className={`
          relative w-36 h-36 rounded-full
          bg-gradient-to-br from-[hsl(252_70%_68%)] to-[hsl(262_60%_58%)]
          shadow-[0_8px_32px_hsl(252_70%_68%/0.4)]
          flex flex-col items-center justify-center
          select-none touch-none
          transition-all duration-150
          ${pressing ? 'scale-95 shadow-[0_4px_16px_hsl(252_70%_68%/0.3)]' : 'scale-100 animate-pulse-glow'}
          active:scale-95
        `}
      >
        <span className="text-3xl mb-1">🛑</span>
        <span className="text-xs font-semibold text-white/90 tracking-wide">STOP</span>
        {pressing && (
          <span className="text-[10px] text-white/70 mt-0.5">
            {Math.round(progress * 100)}%
          </span>
        )}
      </button>
    </div>
  );
}